import React from 'react';
import { ColorPicker, Tooltip } from 'antd';
import { useAppStore } from '../../store/useAppStore';
import { C } from '../../theme';
import { TEXT_FONTS } from '../../paintingConstants';
import { sep, rowStyle, toolBtn, actionBtn } from './toolbarStyles';

interface Props {
  colorPanelRender: (node: React.ReactNode, info: any) => React.ReactNode;
  handleBrushColorChange: (color: any, info: any) => void;
}

const StampTextContextStrip: React.FC<Props> = ({ colorPanelRender, handleBrushColorChange }) => {
  const toolMode               = useAppStore((s) => s.toolMode);
  const stampProjectionMode    = useAppStore((s) => s.stampProjectionMode);
  const setStampProjectionMode = useAppStore((s) => s.setStampProjectionMode);
  const uvViewportMain         = useAppStore((s) => s.uvViewportMain);
  const inverseWarpK           = useAppStore((s) => s.inverseWarpK);
  const setInverseWarpK        = useAppStore((s) => s.setInverseWarpK);
  const inverseWarpStrength    = useAppStore((s) => s.inverseWarpStrength);
  const setInverseWarpStrength = useAppStore((s) => s.setInverseWarpStrength);
  const stampFlipX             = useAppStore((s) => s.stampFlipX);
  const setStampFlipX          = useAppStore((s) => s.setStampFlipX);
  const stampFlipY             = useAppStore((s) => s.stampFlipY);
  const setStampFlipY          = useAppStore((s) => s.setStampFlipY);
  const stampImage             = useAppStore((s) => s.stampImage);
  const setStampPickerOpen     = useAppStore((s) => s.setStampPickerOpen);
  const stampSize              = useAppStore((s) => s.stampSize);
  const setStampSize           = useAppStore((s) => s.setStampSize);
  const stampRotation          = useAppStore((s) => s.stampRotation);
  const setStampRotation       = useAppStore((s) => s.setStampRotation);
  const brushOpacity           = useAppStore((s) => s.brushOpacity);
  const setBrushOpacity        = useAppStore((s) => s.setBrushOpacity);
  const pickerValue            = useAppStore((s) => s.pickerValue);
  const textFont               = useAppStore((s) => s.textFont);
  const setTextFont            = useAppStore((s) => s.setTextFont);
  const textBold               = useAppStore((s) => s.textBold);
  const setTextBold            = useAppStore((s) => s.setTextBold);
  const textItalic             = useAppStore((s) => s.textItalic);
  const setTextItalic          = useAppStore((s) => s.setTextItalic);
  const textJustify            = useAppStore((s) => s.textJustify);
  const setTextJustify         = useAppStore((s) => s.setTextJustify);
  const textLetterSpacing      = useAppStore((s) => s.textLetterSpacing);
  const setTextLetterSpacing   = useAppStore((s) => s.setTextLetterSpacing);
  const textLineHeight         = useAppStore((s) => s.textLineHeight);
  const setTextLineHeight      = useAppStore((s) => s.setTextLineHeight);
  const textContent            = useAppStore((s) => s.textContent);
  const setTextContent         = useAppStore((s) => s.setTextContent);

  return (
    <div className="tb-row" style={{ ...rowStyle, minHeight: 28, overflow: 'visible', position: 'relative', zIndex: 20, borderLeft: `3px solid ${toolMode === 'stamp' ? C.toolStamp : C.toolText}`, paddingLeft: 5 }}>
      <ColorPicker
        disabledAlpha
        mode={['single', 'gradient']}
        value={pickerValue}
        onChange={handleBrushColorChange}
        panelRender={colorPanelRender}
        size="small"
      />
      <div style={sep} />
      {([
        { m: 'wrap',    tip: 'Wrap projection — follows UV space' },
        { m: 'project', tip: uvViewportMain ? 'Project mode unavailable in UV view' : 'Project projection — follows camera view' },
        { m: 'inverse', tip: uvViewportMain ? 'Inverse mode unavailable in UV view' : 'Inverse projection — concave appearance' },
      ] as const).map(({ m, tip }) => (
        <Tooltip key={m} title={tip} placement="bottom">
          <button
            onClick={() => !uvViewportMain || m === 'wrap' ? setStampProjectionMode(m) : undefined}
            disabled={uvViewportMain && m !== 'wrap'}
            style={{ ...toolBtn(stampProjectionMode === m, toolMode === 'stamp' ? 'yellow' : 'purple'), ...(uvViewportMain && m !== 'wrap' ? { opacity: 0.35, cursor: 'not-allowed' } : {}) }}
          >{m}</button>
        </Tooltip>
      ))}
      {stampProjectionMode === 'inverse' && (
        <>
          <div style={sep} />
          <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>K</span>
          <Tooltip title={`Inverse warp K: ${inverseWarpK.toFixed(2)}`} placement="bottom">
            <input type="range" min={0.1} max={4} step={0.05} value={inverseWarpK}
              onChange={(e) => setInverseWarpK(parseFloat(e.target.value))}
              style={{ width: 60, cursor: 'pointer', accentColor: toolMode === 'stamp' ? C.toolStamp : C.toolText }}
            />
          </Tooltip>
          <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Str</span>
          <Tooltip title={`Inverse warp strength: ${inverseWarpStrength.toFixed(2)}`} placement="bottom">
            <input type="range" min={0} max={1} step={0.05} value={inverseWarpStrength}
              onChange={(e) => setInverseWarpStrength(parseFloat(e.target.value))}
              style={{ width: 60, cursor: 'pointer', accentColor: toolMode === 'stamp' ? C.toolStamp : C.toolText }}
            />
          </Tooltip>
        </>
      )}
      <div style={sep} />
      <Tooltip title="Flip stamp horizontally" placement="bottom">
        <button onClick={() => setStampFlipX(!stampFlipX)} style={toolBtn(stampFlipX, toolMode === 'stamp' ? 'yellow' : 'purple')}>Flip X</button>
      </Tooltip>
      <Tooltip title="Flip stamp vertically" placement="bottom">
        <button onClick={() => setStampFlipY(!stampFlipY)} style={toolBtn(stampFlipY, toolMode === 'stamp' ? 'yellow' : 'purple')}>Flip Y</button>
      </Tooltip>
      <div style={sep} />
      {/* Stamp-only: image picker */}
      {toolMode === 'stamp' && (
        <>
          <Tooltip title={stampImage ? 'Change stamp image (or Ctrl+V to paste directly)' : 'Choose stamp image (or Ctrl+V to paste from clipboard)'} placement="bottom">
            <button
              onClick={() => setStampPickerOpen(true)}
              style={actionBtn('yellow')}
            >
              {stampImage ? 'Change stamp…' : 'Pick stamp'}
            </button>
          </Tooltip>
          <div style={sep} />
        </>
      )}
      {/* Text-only: font, style, content, justify, spacing */}
      {toolMode === 'text' && (
        <>
          <select
            value={textFont} onChange={(e) => setTextFont(e.target.value)}
            style={{ fontSize: 11, padding: '2px 4px', borderRadius: 3, background: C.bgDeep, color: C.textPrimary, border: `1px solid rgba(167,139,250,0.4)`, cursor: 'pointer' }}
          >
            {TEXT_FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
          </select>
          <div style={sep} />
          <Tooltip title="Bold" placement="bottom"><button onClick={() => setTextBold(!textBold)} style={{ ...toolBtn(textBold, 'purple'), fontWeight: 'bold' }}>B</button></Tooltip>
          <Tooltip title="Italic" placement="bottom"><button onClick={() => setTextItalic(!textItalic)} style={{ ...toolBtn(textItalic, 'purple'), fontStyle: 'italic' }}>I</button></Tooltip>
          <div style={sep} />
          {/* Justify */}
          <Tooltip title="Align left" placement="bottom"><button onClick={() => setTextJustify('left')} style={toolBtn(textJustify === 'left', 'purple')}>⬅</button></Tooltip>
          <Tooltip title="Align center" placement="bottom"><button onClick={() => setTextJustify('center')} style={toolBtn(textJustify === 'center', 'purple')}>↔</button></Tooltip>
          <Tooltip title="Align right" placement="bottom"><button onClick={() => setTextJustify('right')} style={toolBtn(textJustify === 'right', 'purple')}>➡</button></Tooltip>
          <div style={sep} />
          {/* Kerning */}
          <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Kerning</span>
          <Tooltip title={`${textLetterSpacing.toFixed(2)} em`} placement="bottom">
            <input type="range" min={-0.1} max={0.5} step={0.01} value={textLetterSpacing}
              onChange={(e) => setTextLetterSpacing(Number(e.target.value))}
              style={{ width: 60, cursor: 'pointer', accentColor: C.toolText }}
            />
          </Tooltip>
          <div style={sep} />
          {/* Line Height */}
          <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Line H</span>
          <Tooltip title={`${textLineHeight.toFixed(1)}×`} placement="bottom">
            <input type="range" min={0.8} max={3.0} step={0.1} value={textLineHeight}
              onChange={(e) => setTextLineHeight(Number(e.target.value))}
              style={{ width: 60, cursor: 'pointer', accentColor: C.toolText }}
            />
          </Tooltip>
          <div style={sep} />
          {/* Text content — grows downward as an absolute overlay; toolbar height stays fixed */}
          <div style={{ position: 'relative', width: 160, height: 22, flexShrink: 0, alignSelf: 'center' }}>
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              rows={Math.max(1, textContent.split('\n').length)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                fontSize: 11,
                padding: '3px 6px',
                borderRadius: 3,
                background: C.bgDeep,
                color: C.textPrimary,
                border: `1px solid ${C.border}`,
                width: 160,
                outline: 'none',
                resize: 'none',
                lineHeight: '1.4',
                zIndex: 100,
                boxSizing: 'border-box',
              }}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <div style={sep} />
        </>
      )}
      {/* Shared: stamp/text size and rotation */}
      <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Size</span>
      <Tooltip title={`${(stampSize * 100).toFixed(0)}%`} placement="bottom">
        <input type="range" min={0.01} max={0.4} step={0.005} value={stampSize}
          onChange={(e) => setStampSize(Number(e.target.value))}
          style={{ width: 80, cursor: 'pointer', accentColor: toolMode === 'stamp' ? C.toolStamp : C.toolText }}
        />
      </Tooltip>
      <div style={sep} />
      <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Rotation</span>
      <Tooltip title={`${Math.round(stampRotation)}°`} placement="bottom">
        <input type="range" min={-180} max={180} step={1} value={stampRotation}
          onChange={(e) => setStampRotation(Number(e.target.value))}
          style={{ width: 80, cursor: 'pointer', accentColor: toolMode === 'stamp' ? C.toolStamp : C.toolText }}
        />
      </Tooltip>
      <div style={sep} />
      <span style={{ fontSize: 11, color: C.textDim, flexShrink: 0 }}>Opacity</span>
      <Tooltip title={`${Math.round(brushOpacity * 100)}%`} placement="bottom">
        <input type="range" min={0} max={1} step={0.01} value={brushOpacity}
          onChange={(e) => setBrushOpacity(Number(e.target.value))}
          style={{ width: 70, cursor: 'pointer', accentColor: toolMode === 'stamp' ? C.toolStamp : C.toolText }}
        />
      </Tooltip>
    </div>
  );
};

export default React.memo(StampTextContextStrip);
